import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchableMultiSelect, SearchableSelect } from '../Shared/SearchableSelect';
import TerritoryTreeSelect, { buildTerritoryTree } from '../Shared/TerritoryTreeSelect';
import { lookupService, monitorizareService } from '../../services/data';
import '../../styles/entityPages.css';
import './MonitorizarePage.css';

const emptyForm = {
  marca: '',
  id_client: null,
  data_start_monitorizare: '',
  data_stop_monitorizare: '',
  cuvinte_cheie: '',
  criteria: '',
  clasa_nice: '',
  transmite_email_client: false,
  email_client: '',
  monitorizare_activa: true,
  offices: [],
  territories: [],
  niceclass: [],
};

const emptySelectionState = {
  directTerritories: [],
  directOffices: [],
};

const sameIdSet = (left = [], right = []) => {
  if (left.length !== right.length) {
    return false;
  }

  const leftSet = new Set(left);
  return right.every((item) => leftSet.has(item));
};

const officeRequirementsMet = (linkedTerritoryIds = [], selectedTerritorySet) => {
  if (!linkedTerritoryIds.length) {
    return false;
  }

  return linkedTerritoryIds.every((territoryId) => selectedTerritorySet.has(territoryId));
};

const regionDisplayNames =
  typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const OFFICE_LABEL_OVERRIDES = {
  EM: 'European Union',
  EUIPO: 'European Union Intellectual Property Office',
  WO: 'World Intellectual Property Organization',
  WIPO: 'World Intellectual Property Organization',
  BX: 'Benelux',
  IB: 'International Registration',
};

const formatOfficeLabel = (officeCode, territoryNameByCode) => {
  const code = String(officeCode || '').trim();
  if (!code) {
    return '';
  }

  const upperCode = code.toUpperCase();
  const territoryName = territoryNameByCode.get(upperCode);
  const intlName = upperCode.length === 2 ? regionDisplayNames?.of(upperCode) : '';
  const resolvedName = OFFICE_LABEL_OVERRIDES[upperCode] || territoryName || intlName || '';

  return resolvedName ? `${upperCode} ${resolvedName}` : upperCode;
};

const emptyExcelForm = {
  file: null,
  data_start_monitorizare: '',
  data_stop_monitorizare: '',
};

const MonitorizarePage = () => {
  const [items, setItems] = useState([]);
  const [lookups, setLookups] = useState({
    clienti: [],
    offices: [],
    territories: [],
    niceclasses: [],
    criterias: [],
  });
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectionState, setSelectionState] = useState({ ...emptySelectionState });
  const [expandedTerritories, setExpandedTerritories] = useState({});

  // Excel import modal state
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  const [excelForm, setExcelForm] = useState({ ...emptyExcelForm });
  const [excelImporting, setExcelImporting] = useState(false);
  const [excelResult, setExcelResult] = useState(null); // { created, errors } after import

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [monitorizareData, lookupData] = await Promise.all([
        monitorizareService.list(),
        lookupService.getAll(),
      ]);

      setItems(monitorizareData);
      setLookups({
        clienti: lookupData.clienti || [],
        offices: lookupData.offices || [],
        territories: lookupData.territories || [],
        niceclasses: lookupData.niceclasses || [],
        criterias: lookupData.criterias || [],
      });
    } catch (requestError) {
      setError(requestError.message || 'Nu am putut încărca monitorizarea.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const clientOptions = useMemo(
    () => lookups.clienti.map((item) => ({ value: item.id, label: item.denumire || `Client ${item.id}` })),
    [lookups.clienti]
  );

  const selectedClient = useMemo(
    () => lookups.clienti.find((item) => item.id === form.id_client) || null,
    [lookups.clienti, form.id_client]
  );

  const autoClientEmail = useMemo(() => {
    if (!selectedClient) {
      return '';
    }

    return selectedClient.email_monitorizare || selectedClient.email || '';
  }, [selectedClient]);

  const territoryNameByCode = useMemo(() => {
    const nextMap = new Map();
    lookups.territories.forEach((item) => {
      const code = String(item.territory || '').trim().toUpperCase();
      if (code && item.territory_name && !nextMap.has(code)) {
        nextMap.set(code, item.territory_name);
      }
    });
    return nextMap;
  }, [lookups.territories]);

  const officeOptions = useMemo(
    () => lookups.offices.map((item) => ({
      value: item.id,
      label: formatOfficeLabel(item.office, territoryNameByCode) || `Office ${item.id}`,
    })),
    [lookups.offices, territoryNameByCode]
  );

  const territoryTree = useMemo(() => buildTerritoryTree(lookups.territories), [lookups.territories]);

  const officeToTerritoryIds = useMemo(() => {
    const map = new Map();
    lookups.offices.forEach((office) => {
      map.set(office.id, office.linked_territory_ids || []);
    });
    return map;
  }, [lookups.offices]);

  const effectiveTerritoryIds = useMemo(() => {
    const nextIds = new Set(selectionState.directTerritories);

    selectionState.directOffices.forEach((officeId) => {
      (officeToTerritoryIds.get(officeId) || []).forEach((territoryId) => nextIds.add(territoryId));
    });

    return Array.from(nextIds);
  }, [selectionState.directOffices, selectionState.directTerritories, officeToTerritoryIds]);

  const autoOfficeIds = useMemo(() => {
    const selectedTerritorySet = new Set(selectionState.directTerritories);

    return lookups.offices
      .filter((office) => officeRequirementsMet(office.linked_territory_ids || [], selectedTerritorySet))
      .map((office) => office.id);
  }, [lookups.offices, selectionState.directTerritories]);

  const effectiveOfficeIds = useMemo(() => {
    const nextIds = new Set(selectionState.directOffices);
    autoOfficeIds.forEach((officeId) => nextIds.add(officeId));
    return Array.from(nextIds);
  }, [autoOfficeIds, selectionState.directOffices]);

  const criteriaOptions = useMemo(
    () => lookups.criterias.map((item) => ({ value: item.criteria, label: item.criteria || `Criteria ${item.id}` })),
    [lookups.criterias]
  );

  const niceclassOptions = useMemo(
    () => lookups.niceclasses
      .map((item) => ({ value: item.id, label: item.niceclass || `Class ${item.id}` }))
      .sort((a, b) => {
        const aNum = Number.parseInt(String(a.label), 10);
        const bNum = Number.parseInt(String(b.label), 10);

        if (Number.isNaN(aNum) || Number.isNaN(bNum)) {
          return String(a.label).localeCompare(String(b.label), undefined, { numeric: true });
        }

        return aNum - bNum;
      }),
    [lookups.niceclasses]
  );

  const filteredItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return items;
    }

    return items.filter((item) =>
      [item.marca, item.client_name, item.criteria, item.clasa_nice, item.email_client]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [items, search]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setSelectionState({ ...emptySelectionState });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const applySelectionStateFromItem = useCallback(
    (territoryIds = [], officeIds = []) => {
      const selectedTerritorySet = new Set(territoryIds);
      const derivedOfficeIds = new Set(
        lookups.offices
          .filter((office) => officeRequirementsMet(office.linked_territory_ids || [], selectedTerritorySet))
          .map((office) => office.id)
      );

      setSelectionState({
        directTerritories: territoryIds,
        directOffices: officeIds.filter((officeId) => !derivedOfficeIds.has(officeId)),
      });
    },
    [lookups.offices]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.id_client) {
      setError('Clientul este obligatoriu pentru monitorizare.');
      return;
    }

    try {
      const payload = {
        ...form,
        email_client: autoClientEmail || null,
      };

      if (editingId) {
        await monitorizareService.update(editingId, payload);
      } else {
        await monitorizareService.create(payload);
      }
      await loadData();
      closeModal();
    } catch (requestError) {
      setError(requestError.message || 'Nu am putut salva monitorizarea.');
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      ...emptyForm,
      ...item,
      id_client: item.id_client || null,
      offices: item.offices || [],
      territories: item.territories || [],
      niceclass: item.niceclass || [],
    });
    applySelectionStateFromItem(item.territories || [], item.offices || []);
    setIsModalOpen(true);
  };

  const handleTerritoryTreeChange = useCallback(
    (territoryIds, checked) => {
      setSelectionState((prev) => {
        const nextTerritories = new Set(prev.directTerritories);
        const nextOffices = new Set(prev.directOffices);

        if (checked) {
          territoryIds.forEach((territoryId) => nextTerritories.add(territoryId));
        } else {
          territoryIds.forEach((territoryId) => nextTerritories.delete(territoryId));

          Array.from(nextOffices).forEach((officeId) => {
            const linkedTerritories = officeToTerritoryIds.get(officeId) || [];
            if (linkedTerritories.some((territoryId) => territoryIds.includes(territoryId))) {
              nextOffices.delete(officeId);
            }
          });
        }

        return {
          directTerritories: Array.from(nextTerritories),
          directOffices: Array.from(nextOffices),
        };
      });
    },
    [officeToTerritoryIds]
  );

  const handleOfficeSelectionChange = useCallback(
    (nextOfficeIds) => {
      const nextSet = new Set(nextOfficeIds || []);
      const currentSet = new Set(effectiveOfficeIds);
      const addedOfficeIds = Array.from(nextSet).filter((officeId) => !currentSet.has(officeId));
      const removedOfficeIds = Array.from(currentSet).filter((officeId) => !nextSet.has(officeId));

      setSelectionState((prev) => {
        const nextDirectOffices = new Set(prev.directOffices);
        const nextDirectTerritories = new Set(prev.directTerritories);

        addedOfficeIds.forEach((officeId) => nextDirectOffices.add(officeId));

        removedOfficeIds.forEach((officeId) => {
          nextDirectOffices.delete(officeId);
          (officeToTerritoryIds.get(officeId) || []).forEach((territoryId) => nextDirectTerritories.delete(territoryId));
        });

        return {
          directTerritories: Array.from(nextDirectTerritories),
          directOffices: Array.from(nextDirectOffices),
        };
      });
    },
    [effectiveOfficeIds, officeToTerritoryIds]
  );

  useEffect(() => {
    setForm((prev) => {
      const territoriesChanged = !sameIdSet(prev.territories || [], effectiveTerritoryIds);
      const officesChanged = !sameIdSet(prev.offices || [], effectiveOfficeIds);

      if (!territoriesChanged && !officesChanged) {
        return prev;
      }

      return {
        ...prev,
        territories: effectiveTerritoryIds,
        offices: effectiveOfficeIds,
      };
    });
  }, [effectiveOfficeIds, effectiveTerritoryIds]);

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur vrei să ștergi această monitorizare?')) {
      return;
    }

    setError('');
    try {
      await monitorizareService.remove(id);
      await loadData();
      if (editingId === id) {
        resetForm();
      }
    } catch (requestError) {
      setError(requestError.message || 'Nu am putut șterge monitorizarea.');
    }
  };

  const openExcelModal = () => {
    setExcelForm({ ...emptyExcelForm });
    setExcelResult(null);
    setIsExcelModalOpen(true);
  };

  const closeExcelModal = () => {
    setIsExcelModalOpen(false);
    setExcelForm({ ...emptyExcelForm });
    setExcelResult(null);
  };

  const handleExcelSubmit = async (event) => {
    event.preventDefault();
    if (!excelForm.file) return;

    const formData = new FormData();
    formData.append('file', excelForm.file);
    formData.append('data_start_monitorizare', excelForm.data_start_monitorizare);
    formData.append('data_stop_monitorizare', excelForm.data_stop_monitorizare);

    setExcelImporting(true);
    try {
      const result = await monitorizareService.importExcel(formData);
      setExcelResult(result);
      if (result.created > 0) {
        await loadData();
      }
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail || requestError.message || 'Eroare la import.';
      setExcelResult({ created: 0, errors: [{ row: '-', trademark_name: '', applicant_name: '', reason: detail }] });
    } finally {
      setExcelImporting(false);
    }
  };

  const toggleTerritories = useCallback((itemId) => {
    setExpandedTerritories((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  }, []);

  return (
    <div className="entity-page">
      <div className="entity-page-header">
        <p>Editeaza si adauga monitorizari.</p>
      </div>

      {error && <div className="entity-error">{error}</div>}

      <div className="entity-page-toolbar">
        <div className="entity-toolbar-actions">
          <button type="button" className="btn-primary" onClick={openCreateModal}>
            Adaugă monitorizare
          </button>
          <button type="button" className="btn-primary" onClick={openExcelModal}>
            Încarcă Excel
          </button>
        </div>
        <input
          className="entity-search"
          type="text"
          placeholder="Caută monitorizare..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className="entity-table-wrap">
        {loading ? (
          <div className="entity-loading">Se încarcă...</div>
        ) : (
          <table className="entity-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Marca</th>
                <th>Client</th>
                <th>Perioadă</th>
                <th>Email</th>
                <th>Activă</th>
                {/* <th>Offices</th> */}
                <th>Teritorii</th>
                {/* <th>Nice classes</th> */}
                <th>Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id}>
                  <td data-label="#">{item.id}</td>
                  <td data-label="Marca">{item.marca || '-'}</td>
                  <td data-label="Client">{item.client_name || '-'}</td>
                  <td data-label="Perioadă">
                    {(item.data_start_monitorizare || '-')}
                    {' → '}
                    {(item.data_stop_monitorizare || '-')}
                  </td>
                  <td data-label="Email">{item.email_client || '-'}</td>
                  <td data-label="Activă">{item.monitorizare_activa ? 'Da' : 'Nu'}</td>
                  {/* <td data-label="Offices">
                    {item.office_names?.map((office) => formatOfficeLabel(office, territoryNameByCode)).join(', ') || '-'}
                  </td> */}
                  <td data-label="Territories">
                    {item.territory_names?.length ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                        <span>
                          {(expandedTerritories[item.id] ? item.territory_names : item.territory_names.slice(0, 3)).join(', ')}
                          {!expandedTerritories[item.id] && item.territory_names.length > 3 ? '...' : ''}
                        </span>
                        {item.territory_names.length > 3 && (
                          <button
                            type="button"
                            className="show-more-btn"
                            style={{ padding: 0 }}
                            onClick={() => toggleTerritories(item.id)}
                          >
                            {expandedTerritories[item.id] ? 'Show less' : 'Show all'}
                          </button>
                        )}
                      </div>
                    ) : '-'}
                  </td>
                  {/* <td data-label="Nice classes">{item.niceclass_names?.join(', ') || '-'}</td> */}
                  <td>
                    <div className="table-actions">
                      <button type="button" className="btn-inline" onClick={() => handleEdit(item)}>
                        Edit
                      </button>
                      <button type="button" className="btn-inline danger" onClick={() => handleDelete(item.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filteredItems.length && (
                <tr>
                  <td colSpan={10} className="entity-empty">
                    Nu există date.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {isExcelModalOpen && (
        <div className="entity-modal-overlay" onClick={closeExcelModal}>
          <div className="entity-modal excel-import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="entity-modal-header">
              <h3>Încarcă Excel</h3>
              <button type="button" className="entity-modal-close" onClick={closeExcelModal}>×</button>
            </div>

            {excelResult === null ? (
              <form className="entity-form entity-form-in-modal" onSubmit={handleExcelSubmit}>
                <div className="entity-form-grid">
                  <div className="entity-field entity-field-full">
                    <label>
                      Fișier Excel <span className="entity-required-marker">*</span>
                    </label>
                    <label className="excel-file-drop">
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        onChange={(e) => setExcelForm((prev) => ({ ...prev, file: e.target.files[0] || null }))}
                      />
                      {excelForm.file ? (
                        <span className="excel-file-name">📄 {excelForm.file.name}</span>
                      ) : (
                        <span className="excel-file-placeholder">Click pentru a selecta fișierul Excel (.xlsx)</span>
                      )}
                    </label>
                    <span className="entity-field-hint">
                      Fișierul trebuie să conțină coloanele: Trade mark name, Trade mark office,
                      Designated territory, Goods and Services, Applicant name.
                    </span>
                  </div>

                  <div className="entity-field">
                    <label>
                      Start monitorizare <span className="entity-required-marker">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={excelForm.data_start_monitorizare}
                      onChange={(e) => setExcelForm((prev) => ({ ...prev, data_start_monitorizare: e.target.value }))}
                    />
                  </div>

                  <div className="entity-field">
                    <label>
                      Stop monitorizare <span className="entity-required-marker">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={excelForm.data_stop_monitorizare}
                      onChange={(e) => setExcelForm((prev) => ({ ...prev, data_stop_monitorizare: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="entity-form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={!excelForm.file || !excelForm.data_start_monitorizare || !excelForm.data_stop_monitorizare || excelImporting}
                  >
                    {excelImporting ? 'Se importă...' : 'Importă'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={closeExcelModal} disabled={excelImporting}>
                    Anulează
                  </button>
                </div>
              </form>
            ) : (
              <div className="entity-form entity-form-in-modal">
                <div className="excel-import-result">
                  <p className="excel-import-summary">
                    <strong>{excelResult.created}</strong> monitorizări create cu succes.
                    {excelResult.skipped > 0 && (
                      <> <strong>{excelResult.skipped}</strong> sărite (există deja).</>
                    )}
                    {excelResult.errors.length > 0 && (
                      <> <strong className="excel-import-error-count">{excelResult.errors.length}</strong> erori.</>
                    )}
                  </p>

                  {excelResult.errors.length > 0 && (
                    <div className="excel-import-errors">
                      <p className="excel-import-errors-title">Erori la import:</p>
                      <div className="excel-import-errors-list">
                        {excelResult.errors.map((err, idx) => (
                          <div key={idx} className="excel-import-error-row">
                            <span className="excel-error-row-num">Rând {err.row}</span>
                            {err.trademark_name && (
                              <span className="excel-error-tm">{err.trademark_name}</span>
                            )}
                            {err.applicant_name && (
                              <span className="excel-error-client">{err.applicant_name}</span>
                            )}
                            <span className="excel-error-reason">{err.reason}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="entity-form-actions">
                  <button type="button" className="btn-primary" onClick={closeExcelModal}>
                    Închide
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="entity-modal-overlay" onClick={closeModal}>
          <div className="entity-modal" onClick={(event) => event.stopPropagation()}>
            <div className="entity-modal-header">
              <h3>{editingId ? 'Edit monitorizare' : 'Adaugă monitorizare'}</h3>
              <button type="button" className="entity-modal-close" onClick={closeModal}>×</button>
            </div>

            <form className="entity-form entity-form-in-modal" onSubmit={handleSubmit}>
              <div className="entity-form-grid">
                <div className="entity-field">
                  <label>Marca</label>
                  <input
                    type="text"
                    value={form.marca || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, marca: event.target.value }))}
                  />
                </div>

                <div className="entity-field">
                  <label>
                    Client <span className="entity-required-marker">*</span>
                  </label>
                  <SearchableSelect
                    options={clientOptions}
                    value={form.id_client}
                    onChange={(value) => setForm((prev) => ({ ...prev, id_client: value }))}
                    placeholder="Caută client..."
                  />
                </div>

                <div className="entity-field">
                  <label>Start monitorizare</label>
                  <input
                    type="date"
                    value={form.data_start_monitorizare || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, data_start_monitorizare: event.target.value }))}
                  />
                </div>

                <div className="entity-field">
                  <label>Stop monitorizare</label>
                  <input
                    type="date"
                    value={form.data_stop_monitorizare || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, data_stop_monitorizare: event.target.value }))}
                  />
                </div>

                <div className="entity-field entity-field-full">
                  <label>Cuvinte cheie</label>
                  <textarea
                    rows={3}
                    value={form.cuvinte_cheie || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, cuvinte_cheie: event.target.value }))}
                  />
                </div>

                <div className="entity-field entity-field-full">
                  <label>Criterii</label>
                  <SearchableSelect
                    options={criteriaOptions}
                    value={form.criteria || null}
                    onChange={(value) => setForm((prev) => ({ ...prev, criteria: value || '' }))}
                    placeholder="Selectează criterii..."
                  />
                </div>

                <div className="entity-field">
                  <label>Clasă Nice (text)</label>
                  <input
                    type="text"
                    value={form.clasa_nice || ''}
                    onChange={(event) => setForm((prev) => ({ ...prev, clasa_nice: event.target.value }))}
                  />
                </div>

                <div className="entity-field">
                  <label>Email client (din client)</label>
                  <input
                    type="email"
                    value={autoClientEmail || form.email_client || ''}
                    disabled
                    readOnly
                    placeholder="Se completează automat din client"
                  />
                  <span className="entity-field-hint">
                    Email-ul este preluat automat din câmpurile clientului.
                  </span>
                </div>

                <div className="entity-field entity-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!form.transmite_email_client}
                      onChange={(event) => setForm((prev) => ({ ...prev, transmite_email_client: event.target.checked }))}
                    />
                    <span>Trimite email client</span>
                  </label>
                </div>

                <div className="entity-field entity-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!form.monitorizare_activa}
                      onChange={(event) => setForm((prev) => ({ ...prev, monitorizare_activa: event.target.checked }))}
                    />
                    <span>Monitorizare activă</span>
                  </label>
                </div>

                <div className="entity-field entity-field-full">
                  <label>Territories (tree select)</label>
                  <TerritoryTreeSelect
                    regions={territoryTree}
                    selectedTerritoryIds={effectiveTerritoryIds}
                    onToggleTerritories={handleTerritoryTreeChange}
                    placeholder="Select territories"
                  />
                  <span className="entity-field-hint">
                    Poți selecta regiuni, subregiuni sau teritorii individuale. Modificările rămân doar în formular până la salvare.
                  </span>
                </div>

                <div className="entity-field entity-field-full">
                  <label>Offices (multi-select + căutare)</label>
                  <SearchableMultiSelect
                    options={officeOptions}
                    values={effectiveOfficeIds}
                    onChange={handleOfficeSelectionChange}
                  />
                  <span className="entity-field-hint">
                    Selectarea teritoriilor bifează automat oficiile asociate, iar selectarea unui oficiu completează teritoriile lui.
                  </span>
                </div>

                <div className="entity-field entity-field-full">
                  <label>Nice classes (multi-select + căutare)</label>
                  <SearchableMultiSelect
                    options={niceclassOptions}
                    values={form.niceclass || []}
                    onChange={(values) => setForm((prev) => ({ ...prev, niceclass: values }))}
                  />
                </div>
              </div>

              <div className="entity-form-actions">
                <button type="submit" className="btn-primary">
                  {editingId ? 'Actualizează monitorizare' : 'Adaugă monitorizare'}
                </button>
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Anulează
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitorizarePage;
